import React, { useEffect, useState } from "react";
import { monthsForDropdown, years } from "./constants";
import { fetchCohortReport, fetchPlanNames } from "./api";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  Grid,
  Select,
  Button,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import PlanCohortTable from "./PlanCohortTable";

const ThaCohortReport = () => {
  const [data, setData] = useState<any[] | null>(null);
  const [startMonth, setStartMonth] = useState("");
  const [startYear, setStartYear] = useState("");
  const [plans, setPlans] = useState<any[]>([]);
  const [endMonth, setEndMonth] = useState("");
  const [action, setAction] = useState(false);
  const [endYear, setEndYear] = useState("");
  const [planId, setPlanId] = useState("");
  const controller = new AbortController();

  useEffect(() => {
    return () => controller.abort();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const plansData = await fetchPlanNames(controller.signal);
        setPlans(plansData);
      } catch (error) {
        console.error("Failed to fetch plans", error);
      }
    })();
  }, []);
  const fetchDataFromAPI = async () => {
    toast.dismiss();
    setAction(true);
    setData(null);
    try {
      const payload = {
        StartMonth: startMonth,
        StartYear: startYear,
        EndMonth: endMonth,
        EndYear: endYear,
        PlanName: planId,
      };

      const data = await fetchCohortReport(payload, controller.signal);

      if (Array.isArray(data) && data.length > 0) {
        setData(data);
      } else {
        toast.info("No record Found");
      }
      setAction(false);
    } catch (error) {
      setAction(false);
    }
  };
  const isDateRangeValid = (
    startM: string,
    startY: string,
    endM: string,
    endY: string
  ) => {
    // Use monthsForDropdown for index lookup
    const startIndex = monthsForDropdown.findIndex(
      (m: any) => m.value === startM
    );
    const endIndex = monthsForDropdown.findIndex((m: any) => m.value === endM);
    const start = new Date(`${startY}-${startIndex + 1}-01`);
    const end = new Date(`${endY}-${endIndex + 1}-01`);
    return (
      startIndex !== -1 &&
      endIndex !== -1 &&
      !isNaN(start.getTime()) &&
      !isNaN(end.getTime()) &&
      start < end
    );
  };

  const isSearchEnabled =
    startMonth &&
    startYear &&
    endMonth &&
    endYear &&
    planId &&
    isDateRangeValid(startMonth, startYear, endMonth, endYear);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Plan Cohort Analysis</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <fieldset>
                <legend>Start Params:</legend>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{
                          background: "#fff",
                          padding: " 0 5px 0px 0px",
                        }}
                      >
                        Start Month
                      </InputLabel>
                      <Select
                        value={startMonth}
                        onChange={(e) => setStartMonth(e.target.value)}
                      >
                        {monthsForDropdown?.map((m: any) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{
                          background: "#fff",
                          padding: " 0 5px 0px 0px",
                        }}
                      >
                        Start Year
                      </InputLabel>
                      <Select
                        value={startYear}
                        onChange={(e) => setStartYear(e.target.value)}
                      >
                        {years?.map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>

            <Grid item xs={12}>
              <fieldset>
                <legend>End Params:</legend>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{
                          background: "#fff",
                          padding: " 0 5px 0px 0px",
                        }}
                      >
                        End Month
                      </InputLabel>
                      <Select
                        value={endMonth}
                        onChange={(e) => setEndMonth(e.target.value)}
                      >
                        {monthsForDropdown?.map((m: any) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{
                          background: "#fff",
                          padding: " 0 5px 0px 0px",
                        }}
                      >
                        End Year
                      </InputLabel>
                      <Select
                        value={endYear}
                        onChange={(e) => setEndYear(e.target.value)}
                      >
                        {years?.map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{
                          background: "#fff",
                          padding: "0 5px 0 0",
                        }}
                      >
                        Plan Name
                      </InputLabel>
                      <Select
                        value={planId}
                        onChange={(e) => setPlanId(e.target.value)}
                      >
                        {plans?.map((plan) => (
                          <MenuItem
                            key={plan.Calling_package_Id}
                            value={plan.Plan_Name}
                          >
                            {plan.Plan_Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </fieldset>
            </Grid>

            <Grid
              item
              xs={4}
              style={{
                margin: "0 auto",
              }}
            >
              {!action && (
                <Button
                  className="dButton dButton2"
                  variant="contained"
                  onClick={fetchDataFromAPI}
                  disabled={!isSearchEnabled}
                >
                  Search
                </Button>
              )}

              {action && (
                <Button className="dButton dButton2" variant="contained">
                  <CircularProgress color="inherit" size={30} />
                </Button>
              )}
            </Grid>
          </Grid>

          <div style={{ marginTop: "40px" }}>
            <PlanCohortTable data={data} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ThaCohortReport;
